import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import http from '../../http-common';
import { Box, Grid, Card, CardContent, Typography, Button, CircularProgress, Snackbar, Alert, Paper, IconButton, Tooltip, Stack, Switch, TextField, Fade, Menu, MenuItem, FormControl, Select, Modal, Backdrop } from '@mui/material';
import { Line, Bar, Doughnut } from 'react-chartjs-2';
import { AiOutlineSearch } from 'react-icons/ai';
import { FaUsers, FaFilter, FaCog, FaExclamationTriangle, FaDownload, FaList, FaInfoCircle, FaSyncAlt, FaExpand, FaTimes } from 'react-icons/fa';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import 'chart.js/auto';

const statCards = [
  { key: 'totalCustomers', label: 'Customers', icon: <FaUsers className="icon" style={{ color: 'var(--color-primary)' }} /> },
  { key: 'totalFilters', label: 'Filters', icon: <FaFilter className="icon" style={{ color: 'var(--color-accent)' }} /> },
  { key: 'totalInstalledFilters', label: 'Installed Filters', icon: <FaCog className="icon" style={{ color: 'var(--color-warning)' }} /> },
  { key: 'overdueFilters', label: 'Overdue Filters', icon: <FaExclamationTriangle className="icon" style={{ color: 'var(--color-danger)' }} /> },
];

const monthLabels = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const defaultChartConfigs = [
  {
    key: 'monthlyGrowth',
    type: 'line',
    defaultTitle: 'Monthly Customer Growth',
    info: 'Shows the number of new customers added each month this year.'
  },
  {
    key: 'filtersInstalledPerMonth',
    type: 'bar',
    defaultTitle: 'Filters Installed Per Month',
    info: 'Shows how many filters were installed each month this year.'
  },
  {
    key: 'overdueVsNotOverdue',
    type: 'doughnut',
    defaultTitle: 'Overdue vs Not Overdue Filters',
    info: 'Compares the number of overdue filters to those not overdue.'
  },
  {
    key: 'filterTypeDistribution',
    type: 'doughnut',
    defaultTitle: 'Filter Type Distribution',
    info: 'Shows the distribution of filter types in your system.'
  },
  {
    key: 'filterStatusOverview',
    type: 'bar',
    defaultTitle: 'Filter Status Overview',
    info: 'Shows the number of filters that are active, overdue, or due soon.'
  },
  {
    key: 'topCustomers',
    type: 'bar',
    defaultTitle: 'Top Customers by Installed Filters',
    info: 'Shows the customers with the most installed filters.'
  }
];

const palette = {
  primary: '#10277B',
  accent: '#28a745',
  danger: '#dc3545',
  warning: '#ffc107',
  info: '#17a2b8',
  background: '#f5f5f5',
  text: '#10277B',
  muted: '#a1a1a1',
  card: '#fff',
  cardDark: '#1a1a2e',
  // New chart color palette - shades of blue and grey
  chartBlue1: '#10277B',
  chartBlue2: '#2E5BBA',
  chartBlue3: '#4A90E2',
  chartBlue4: '#6BB6FF',
  chartBlue5: '#87CEEB',
  chartGrey1: '#708090',
  chartGrey2: '#9CA3AF',
  chartGrey3: '#D1D5DB',
  chartGrey4: '#E5E7EB',
  chartGrey5: '#F3F4F6',
};

const getChartData = (key, data) => {
  const chartColors = [palette.chartBlue1, palette.chartBlue2, palette.chartBlue3, palette.chartGrey1, palette.chartGrey2, palette.chartGrey3];
  
  switch (key) {
    case 'monthlyGrowth':
      return {
        labels: monthLabels,
        datasets: [{
          label: 'New Customers',
          data: data?.monthlyGrowth ?? [],
          borderColor: palette.chartBlue1,
          backgroundColor: 'rgba(16,39,123,0.1)',
          fill: true,
          tension: 0.4,
          pointRadius: 5,
          pointHoverRadius: 7,
        }]
      };
    case 'filtersInstalledPerMonth':
      return {
        labels: monthLabels,
        datasets: [{
          label: 'Filters Installed',
          data: data?.filtersInstalledPerMonth ?? [],
          backgroundColor: palette.chartBlue2,
          borderColor: palette.chartBlue1,
          borderWidth: 2
        }]
      };
    case 'overdueVsNotOverdue':
      return {
        labels: ['Overdue', 'Not Overdue'],
        datasets: [{
          data: [
            data?.overdueVsNotOverdue?.overdue ?? 0,
            data?.overdueVsNotOverdue?.notOverdue ?? 0
          ],
          backgroundColor: [palette.danger, palette.chartBlue1], // Keep overdue red
          borderWidth: 2,
          borderColor: '#fff'
        }]
      };
    case 'filterTypeDistribution':
      return {
        labels: data?.filterTypeDistribution?.labels ?? [],
        datasets: [{
          data: data?.filterTypeDistribution?.counts ?? [],
          backgroundColor: chartColors,
          borderWidth: 2,
          borderColor: '#fff'
        }]
      };
    case 'filterStatusOverview':
      return {
        labels: ['Active', 'Overdue', 'Due Soon'],
        datasets: [{
          data: [
            data?.filterStatusOverview?.active ?? 0,
            data?.filterStatusOverview?.overdue ?? 0,
            data?.filterStatusOverview?.dueSoon ?? 0
          ],
          backgroundColor: [palette.chartBlue1, palette.danger, palette.chartBlue3], // Keep overdue red
          borderWidth: 2,
          borderColor: '#fff'
        }]
      };
    case 'topCustomers':
      return {
        labels: (data?.topCustomers ?? []).map(c => c.name),
        datasets: [{
          label: 'Installed Filters',
          data: (data?.topCustomers ?? []).map(c => c.count),
          backgroundColor: palette.chartBlue1,
          borderColor: palette.chartBlue2,
          borderWidth: 2
        }]
      };
    default:
      return {};
  }
};

const chartTypeComponent = {
  line: Line,
  bar: Bar,
  doughnut: Doughnut
};

const Dashboard = () => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [chartTitles, setChartTitles] = useState(() => {
    const saved = localStorage.getItem('dashboardChartTitles');
    return saved ? JSON.parse(saved) : Object.fromEntries(defaultChartConfigs.map(c => [c.key, c.defaultTitle]));
  });
  const [showCharts] = useState(() => {
    const saved = localStorage.getItem('dashboardShowCharts');
    return saved ? JSON.parse(saved) : Object.fromEntries(defaultChartConfigs.map(c => [c.key, true]));
  });
  const [chartOrder] = useState(() => {
    const saved = localStorage.getItem('dashboardChartOrder');
    return saved ? JSON.parse(saved) : defaultChartConfigs.map(c => c.key);
  });
  const [darkMode] = useState(() => {
    const saved = localStorage.getItem('dashboardDarkMode');
    return saved ? JSON.parse(saved) : false;
  });
  const [autoRefresh, setAutoRefresh] = useState(false);
  const chartRefs = {
    monthlyGrowth: useRef(),
    filtersInstalledPerMonth: useRef(),
    overdueVsNotOverdue: useRef(),
    filterTypeDistribution: useRef(),
    filterStatusOverview: useRef(),
    topCustomers: useRef(),
  };
  const [exportAnchorEls, setExportAnchorEls] = useState({});
  const [exportAllAnchorEl, setExportAllAnchorEl] = useState(null);
  const [chartSearchTerm, setChartSearchTerm] = useState("");
  const [refreshInterval, setRefreshInterval] = useState(30);
  const [dateRange, setDateRange] = useState('all');
  const [modalChart, setModalChart] = useState(null);

  const handleStatCardClick = (key) => {
    if (key === 'overdueFilters') {
      navigate('/customers-filters?status=overdue');
    }
  };

  const fetchData = useCallback(
    async ({ range = dateRange, showLoader = true } = {}) => {
      if (showLoader) {
        setLoading(true);
      }
      try {
        const res = await http.get('/dashboard/summary', {
          params: { range },
        });
        setData(res.data);
        setError(null);
      } catch (err) {
        console.error('Dashboard fetch error:', err);
        setError('Failed to load dashboard data');
      } finally {
        if (showLoader) {
          setLoading(false);
        }
      }
    },
    [dateRange]
  );

  useEffect(() => {
    fetchData();
    let interval;
    if (autoRefresh) {
      const intervalMs = Number(refreshInterval) * 1000;
      interval = setInterval(() => fetchData({ showLoader: false }), intervalMs);
    }
    return () => interval && clearInterval(interval);
  }, [autoRefresh, refreshInterval, fetchData]);

  // Export helpers
  const exportChartAsImage = async (ref, key) => {
    if (!ref.current) return;
    const canvas = await html2canvas(ref.current);
    const link = document.createElement('a');
    const title = chartTitles[key] || key;
    link.download = `${title}.png`;
    link.href = canvas.toDataURL();
    link.click();
  };

  const exportChartAsPDF = async (ref, key) => {
    if (!ref.current) return;
    const canvas = await html2canvas(ref.current);
    const imgData = canvas.toDataURL('image/png');
    const pdf = new jsPDF();
    const title = chartTitles[key] || key;
    pdf.addImage(imgData, 'PNG', 10, 10, 190, 90);
    pdf.save(`${title}.pdf`);
  };

  const exportAllCharts = async (type = 'image') => {
    for (const key of chartOrder) {
      if (showCharts[key]) {
        if (type === 'image') await exportChartAsImage(chartRefs[key], key);
        else await exportChartAsPDF(chartRefs[key], key);
      }
    }
    setSnackbar({ open: true, message: `Exported all visible charts as ${type === 'image' ? 'images' : 'PDFs'}`, severity: 'success' });
  };

  // Chart title editing
  const handleTitleChange = (key, value) => {
    setChartTitles(titles => {
      const updated = { ...titles, [key]: value };
      localStorage.setItem('dashboardChartTitles', JSON.stringify(updated));
      return updated;
    });
  };

  // Animated stat card count-up
  const AnimatedNumber = ({ value }) => {
    const [display, setDisplay] = useState(0);
    useEffect(() => {
      let start = 0;
      const end = value;
      if (start === end) return;
      let increment = end > start ? 1 : -1;
      let stepTime = Math.abs(Math.floor(1000 / (end - start)));
      let timer = setInterval(() => {
        start += increment;
        setDisplay(start);
        if (start === end) clearInterval(timer);
      }, stepTime);
      return () => clearInterval(timer);
    }, [value]);
    return <span>{display}</span>;
  };

  // Dynamic theme
  const dynamicPalette = darkMode ? {
    ...palette,
    background: '#181a1b',
    card: palette.cardDark,
    text: '#fff',
    muted: '#b0b0b0',
  } : palette;

  // Export menu handlers
  const handleExportMenuOpen = (event, key) => {
    setExportAnchorEls(prev => ({ ...prev, [key]: event.currentTarget }));
  };
  const handleExportMenuClose = (key) => {
    setExportAnchorEls(prev => ({ ...prev, [key]: null }));
  };

  // Export all charts menu handlers
  const handleExportAllMenuOpen = (event) => {
    setExportAllAnchorEl(event.currentTarget);
  };
  const handleExportAllMenuClose = () => {
    setExportAllAnchorEl(null);
  };

  // Remove Excel export function and update export handler
  const handleExport = async (type, key) => {
    if (type === 'image') await exportChartAsImage(chartRefs[key], key);
    else if (type === 'pdf') await exportChartAsPDF(chartRefs[key], key);
    handleExportMenuClose(key);
  };

  // Updated export all handler (remove Excel)
  const handleExportAll = async (type) => {
    await exportAllCharts(type);
    handleExportAllMenuClose();
  };

  // Chart modal handlers
  const handleChartClick = (key) => {
    setModalChart(key);
  };

  const handleCloseModal = () => {
    setModalChart(null);
  };

  // Filter visible charts based on search term
  const filteredChartOrder = chartOrder.filter(key => {
    const cfg = defaultChartConfigs.find(c => c.key === key);
    if (!cfg || !showCharts[key]) return false;
    return chartTitles[key].toLowerCase().includes(chartSearchTerm.toLowerCase()) ||
           cfg.defaultTitle.toLowerCase().includes(chartSearchTerm.toLowerCase());
  });

  if (loading) return <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh"><CircularProgress /></Box>;
  if (error) return <Box display="flex" flexDirection="column" alignItems="center" mt={8}><Typography color="error" variant="h6">{error}</Typography><Button onClick={() => fetchData()} variant="contained" sx={{ mt: 2 }}>Retry</Button></Box>;

  return (
    <Box sx={{ p: { xs: 1, md: 4 }, background: dynamicPalette.background, minHeight: '100vh', fontFamily: 'var(--font-main)', transition: 'background 0.3s' }}>
      {/* Header with title and controls */}
      <Stack direction="row" alignItems="center" justifyContent="space-between" mb={3}>
        <Typography variant="h4" fontWeight={700} color={dynamicPalette.text} sx={{ fontFamily: 'var(--font-heading)', color: 'var(--color-primary)' }}>Dashboard</Typography>
        <Stack direction="row" spacing={2} alignItems="center">
          <Tooltip title="Export All Charts">
            <span>
              <IconButton className="icon" onClick={handleExportAllMenuOpen} sx={{ fontSize: '1rem' }}>
                <FaDownload />
              </IconButton>
              <Menu
                anchorEl={exportAllAnchorEl}
                open={Boolean(exportAllAnchorEl)}
                onClose={handleExportAllMenuClose}
              >
                <MenuItem onClick={() => handleExportAll('image')}>Export All as Image</MenuItem>
                <MenuItem onClick={() => handleExportAll('pdf')}>Export All as PDF</MenuItem>
              </Menu>
            </span>
          </Tooltip>
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <Select
              value={refreshInterval}
              onChange={e => setRefreshInterval(Number(e.target.value))}
              displayEmpty
              className="dashboard-select"
              sx={{
                '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
                '& .MuiSelect-select': { 
                  borderBottom: '2px solid var(--color-border)',
                  paddingBottom: '8px',
                  fontSize: '16px',
                  fontFamily: 'var(--font-main)'
                },
                '&:hover .MuiSelect-select': { borderBottomColor: 'var(--color-primary)' },
                '&.Mui-focused .MuiSelect-select': { borderBottomColor: 'var(--color-primary)' }
              }}
            >
              <MenuItem value={10}>10s</MenuItem>
              <MenuItem value={30}>30s</MenuItem>
              <MenuItem value={60}>1m</MenuItem>
              <MenuItem value={300}>5m</MenuItem>
            </Select>
          </FormControl>
          <Tooltip title="Auto-refresh"><Switch checked={autoRefresh} onChange={e => setAutoRefresh(e.target.checked)} color="primary" /></Tooltip>
          <Tooltip title="Refresh now"><IconButton className="icon" onClick={() => fetchData()} sx={{ fontSize: '1rem' }}><FaSyncAlt /></IconButton></Tooltip>
        </Stack>
      </Stack>

      {/* Stats Cards */}
      <Grid container spacing={3} mb={2}>
        {statCards.map(card => {
          const isClickable = card.key === 'overdueFilters';
          return (
            <Grid item xs={12} sm={6} md={3} key={card.key}>
              <Card
                onClick={isClickable ? () => handleStatCardClick(card.key) : undefined}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  p: 2,
                  boxShadow: 3,
                  borderRadius: 3,
                  background: dynamicPalette.card,
                  transition: 'background 0.3s, transform 0.2s ease, box-shadow 0.2s ease',
                  cursor: isClickable ? 'pointer' : 'default',
                  ...(isClickable
                    ? {
                        '&:hover': {
                          boxShadow: 5,
                          transform: 'translateY(-4px)'
                        }
                      }
                    : {})
                }}
              >
                <Box mr={2}>{card.icon}</Box>
                <Box>
                  <Typography variant="h5" fontWeight={700} color={dynamicPalette.text}><AnimatedNumber value={data[card.key] || 0} /></Typography>
                  <Typography color="text.secondary">{card.label}</Typography>
                </Box>
              </Card>
            </Grid>
          );
        })}
      </Grid>

      {/* Chart Controls */}
      <Box mb={3} sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
        <div className="search-bar-wrapper">
          <span className="search-icon">
            <AiOutlineSearch />
          </span>
          <input
            type="text"
            placeholder="Search Charts..."
            className="search-bar"
            value={chartSearchTerm}
            onChange={e => setChartSearchTerm(e.target.value)}
          />
        </div>
        <FormControl size="small" sx={{ minWidth: 120 }}>
          <Select
            value={dateRange}
            onChange={e => setDateRange(e.target.value)}
            displayEmpty
            className="dashboard-select"
            sx={{
              '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
              '& .MuiSelect-select': { 
                borderBottom: '2px solid var(--color-border)',
                paddingBottom: '8px',
                fontSize: '16px',
                fontFamily: 'var(--font-main)'
              },
              '&:hover .MuiSelect-select': { borderBottomColor: 'var(--color-primary)' },
              '&.Mui-focused .MuiSelect-select': { borderBottomColor: 'var(--color-primary)' }
            }}
          >
            <MenuItem value="all">All Time</MenuItem>
            <MenuItem value="7d">Last 7 Days</MenuItem>
            <MenuItem value="30d">Last 30 Days</MenuItem>
            <MenuItem value="90d">Last 90 Days</MenuItem>
            <MenuItem value="1y">Last Year</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {/* Charts Grid - All same size */}
      <Grid container spacing={3} mb={3}>
        {filteredChartOrder.map((key, idx) => {
                const cfg = defaultChartConfigs.find(c => c.key === key);
                if (!cfg) return null;
                      const ChartComponent = chartTypeComponent[cfg.type];
                      return (
            <Grid item xs={12} md={6} key={key} sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                          <Fade in timeout={500}>
                <Paper 
                  onClick={() => handleChartClick(key)}
                  sx={{ 
                    p: 3, 
                    borderRadius: 3, 
                    boxShadow: 2, 
                    background: dynamicPalette.card, 
                    position: 'relative', 
                    minHeight: 400, 
                    maxHeight: 400, 
                    height: 400, 
                    minWidth: 600, 
                    maxWidth: 600, 
                    width: 600, 
                    transition: 'all 0.3s', 
                    display: 'flex', 
                    flexDirection: 'column', 
                    justifyContent: 'space-between',
                    border: '1px solid var(--color-border)',
                    cursor: 'pointer',
                    alignItems: 'center',
                    '&:hover': {
                      boxShadow: 4,
                      transform: 'translateY(-2px)'
                    }
                  }}
                >
                  <Box display="flex" alignItems="center" justifyContent="space-between" mb={2} sx={{ width: '100%' }}>
                                <Box display="flex" alignItems="center" gap={1}>
                      <Typography
                        variant="h6"
                        sx={{ 
                          fontWeight: 600, 
                          fontSize: 18, 
                          color: 'var(--color-primary)', 
                          fontFamily: 'var(--font-heading)' 
                        }}
                      >
                        {chartTitles[key]}
                      </Typography>
                      <Tooltip title={cfg.info}><span><FaInfoCircle color="var(--color-info)" style={{ fontSize: '1rem' }} /></span></Tooltip>
                                </Box>
                    <Tooltip title="Click to expand">
                      <span><FaExpand color="var(--color-muted)" style={{ fontSize: '1rem' }} /></span>
                                  </Tooltip>
                              </Box>
                  <div ref={chartRefs[key]} style={{ 
                    background: dynamicPalette.card, 
                    borderRadius: 8, 
                    padding: 8, 
                    minHeight: 300, 
                    maxHeight: 300, 
                    height: 300, 
                    minWidth: 540, 
                    maxWidth: 540, 
                    width: 540, 
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                                  <ChartComponent
                                    data={getChartData(key, data)}
                                    options={{
                                      responsive: true,
                        maintainAspectRatio: false,
                        plugins: { 
                          legend: { 
                            display: cfg.type !== 'bar',
                            labels: {
                              font: {
                                family: 'var(--font-main)',
                                size: 11
                              }
                            }
                          }
                        },
                        scales: cfg.type === 'bar' || cfg.type === 'line' ? { 
                          y: { 
                            beginAtZero: true,
                            ticks: {
                              font: {
                                family: 'var(--font-main)',
                                size: 10
                              }
                            }
                          },
                          x: {
                            ticks: {
                              font: {
                                family: 'var(--font-main)',
                                size: 10
                              }
                            }
                          }
                        } : undefined,
                                      animation: { duration: 1000 }
                                    }}
                                  />
                                </div>
                            </Paper>
                          </Fade>
                        </Grid>
                      );
              })}
            </Grid>

      {/* Chart Modal */}
      <Modal
        open={Boolean(modalChart)}
        onClose={handleCloseModal}
        closeAfterTransition
        BackdropComponent={Backdrop}
        BackdropProps={{
          timeout: 500,
          sx: { backgroundColor: 'rgba(0, 0, 0, 0.8)' }
        }}
      >
        <Fade in={Boolean(modalChart)}>
          <Box sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 700,
            height: 600,
            bgcolor: 'background.paper',
            border: '2px solid var(--color-border)',
            borderRadius: 3,
            boxShadow: 24,
            p: 3,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            {modalChart && (() => {
              const cfg = defaultChartConfigs.find(c => c.key === modalChart);
              const ChartComponent = chartTypeComponent[cfg.type];
              return (
                <>
                  <Box display="flex" alignItems="center" justifyContent="space-between" mb={3} sx={{ width: '100%' }}>
                    <Box display="flex" alignItems="center" gap={2} sx={{ width: '100%' }}>
                      <TextField
                        value={chartTitles[modalChart]}
                        onChange={e => handleTitleChange(modalChart, e.target.value)}
                        variant="standard"
                        InputProps={{
                          disableUnderline: true,
                          style: { 
                            fontWeight: 600, 
                            fontSize: 28, 
                            color: 'var(--color-primary)', 
                            fontFamily: 'var(--font-heading)',
                            width: '100%'
                          }
                        }}
                        className="modal-title-input"
                        sx={{ 
                          width: '100%',
                          '& .MuiInputBase-input': {
                            borderBottom: '2px solid #ccc',
                            paddingBottom: '8px',
                            transition: 'border-color 0.3s ease',
                            '&:focus': {
                              borderBottomColor: 'var(--color-primary)'
                            }
                          }
                        }}
                      />
                      <Tooltip title={cfg.info}><span><FaInfoCircle color="var(--color-info)" style={{ fontSize: '1.2rem' }} /></span></Tooltip>
                    </Box>
                    <Stack direction="row" spacing={1} sx={{ ml: 2 }}>
                      <Tooltip title="Export">
                        <span>
                          <IconButton className="icon export-icon" onClick={e => handleExportMenuOpen(e, modalChart)} sx={{ fontSize: '1.2rem' }}>
                            <FaDownload />
                          </IconButton>
                          <Menu
                            anchorEl={exportAnchorEls[modalChart]}
                            open={Boolean(exportAnchorEls[modalChart])}
                            onClose={() => handleExportMenuClose(modalChart)}
                          >
                            <MenuItem onClick={() => handleExport('image', modalChart)}>Export as Image</MenuItem>
                            <MenuItem onClick={() => handleExport('pdf', modalChart)}>Export as PDF</MenuItem>
                          </Menu>
                        </span>
                      </Tooltip>
                      <Tooltip title="Close">
                        <IconButton onClick={handleCloseModal} sx={{ fontSize: '1.2rem' }}>
                          <FaTimes />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  </Box>
                  <Box sx={{ 
                    flex: 1, 
                    width: '100%', 
                    height: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    overflow: 'hidden'
                  }}>
                    <div style={{ 
                      width: 540, 
                      height: 400,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      <ChartComponent
                        data={getChartData(modalChart, data)}
                        options={{
                          responsive: true,
                          maintainAspectRatio: false,
                          plugins: { 
                            legend: { 
                              display: cfg.type !== 'bar',
                              labels: {
                                font: {
                                  family: 'var(--font-main)',
                                  size: 16
                                }
                              }
                            }
                          },
                          scales: cfg.type === 'bar' || cfg.type === 'line' ? { 
                            y: { 
                              beginAtZero: true,
                              ticks: {
                                font: {
                                  family: 'var(--font-main)',
                                  size: 14
                                }
                              }
                            },
                            x: {
                              ticks: {
                                font: {
                                  family: 'var(--font-main)',
                                  size: 14
                                }
                              }
                            }
                          } : undefined,
                          animation: { duration: 1000 }
                        }}
                      />
                    </div>
                  </Box>
                </>
              );
            })()}
          </Box>
        </Fade>
      </Modal>

      {/* Recent Activity Section - matching website design */}
      <Paper sx={{ 
        p: 3, 
        borderRadius: 3, 
        boxShadow: 2, 
        background: dynamicPalette.card,
        border: '1px solid var(--color-border)'
      }}>
        <Box display="flex" alignItems="center" mb={2}>
          <FaList style={{ marginRight: 8, color: 'var(--color-primary)', fontSize: '1rem' }} />
          <Typography variant="h6" fontWeight={600} color="var(--color-primary)" sx={{ fontFamily: 'var(--font-heading)' }}>Recent Activity</Typography>
        </Box>
        <Grid container spacing={2}>
          {data.recentActivity && data.recentActivity.length > 0 ? data.recentActivity.map((item, idx) => (
            <Grid item xs={12} md={6} key={item.id}>
              <Card sx={{ 
                mb: 2, 
                borderLeft: '5px solid var(--color-primary)', 
                borderRadius: 2, 
                boxShadow: 1, 
                background: dynamicPalette.card,
                border: '1px solid var(--color-border)'
              }}>
                <CardContent>
                  <Typography variant="subtitle1" fontWeight={600} color="var(--color-text)" sx={{ fontFamily: 'var(--font-main)' }}>
                    {item.Customer?.firstName} {item.Customer?.lastName} installed <b>{item.Filter?.filter_name}</b>
                  </Typography>
                  <Typography color="var(--color-muted)" fontSize={14} sx={{ fontFamily: 'var(--font-main)' }}>
                    Installed: {item.date_of_installation} | Change Due: {item.date_to_be_changed}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          )) : (
            <Grid item xs={12}><Typography color="var(--color-muted)" sx={{ fontFamily: 'var(--font-main)' }}>No recent activity.</Typography></Grid>
          )}
        </Grid>
      </Paper>
      
      <Snackbar open={snackbar.open} autoHideDuration={3000} onClose={() => setSnackbar({ ...snackbar, open: false })}>
        <Alert severity={snackbar.severity} sx={{ width: '100%' }}>{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
};

export default Dashboard;
